/**
 * AdminCache - Client-side caching utility for Admin Portal
 * 
 * Provides in-memory caching with TTL for frequently accessed data.
 * Reduces API calls and improves dashboard load times.
 */

// Cache storage
const cache = new Map();

// Default TTL values (in milliseconds)
const DEFAULT_TTL = {
  short: 30 * 1000,      // 30 seconds - for rapidly changing data
  medium: 2 * 60 * 1000, // 2 minutes - for moderately changing data
  long: 5 * 60 * 1000,   // 5 minutes - for slowly changing data
  extended: 15 * 60 * 1000 // 15 minutes - for rarely changing data
};

// Cache key prefixes for different data types
const CACHE_KEYS = {
  DASHBOARD_STATS: 'dashboard_stats',
  SYSTEM_HEALTH: 'system_health',
  LAMBDA_METRICS: 'lambda_metrics',
  DATABASE_METRICS: 'database_metrics',
  USER_LIST: 'user_list',
  SUBSCRIPTION_LIST: 'subscription_list',
  PAYMENT_METRICS: 'payment_metrics',
  PROMO_CODES: 'promo_codes',
  WEBHOOK_EVENTS: 'webhook_events',
  ANALYTICS: 'analytics',
  AUDIT_LOGS: 'audit_logs',
  SUPPORT_TICKETS: 'support_tickets',
  FEATURE_FLAGS: 'feature_flags',
  NOTIFICATIONS: 'notifications'
};

/**
 * Set a value in cache with TTL
 * @param {string} key - Cache key
 * @param {any} value - Value to cache
 * @param {number} ttl - Time to live in milliseconds
 */
const set = (key, value, ttl = DEFAULT_TTL.medium) => {
  const expiresAt = Date.now() + ttl;
  cache.set(key, {
    value,
    expiresAt,
    createdAt: Date.now()
  });
};

/**
 * Get a value from cache
 * @param {string} key - Cache key
 * @returns {any|null} - Cached value or null if expired/not found
 */
const get = (key) => {
  const entry = cache.get(key);
  
  if (!entry) {
    return null;
  }
  
  // Check if expired
  if (Date.now() > entry.expiresAt) {
    cache.delete(key);
    return null;
  }
  
  return entry.value;
};

/**
 * Check if a key exists and is not expired
 * @param {string} key - Cache key
 * @returns {boolean}
 */
const has = (key) => {
  const entry = cache.get(key);
  if (!entry) return false;
  if (Date.now() > entry.expiresAt) {
    cache.delete(key);
    return false;
  }
  return true;
};

/**
 * Delete a specific key from cache
 * @param {string} key - Cache key
 */
const remove = (key) => {
  cache.delete(key);
};

/**
 * Clear all cache entries
 */
const clear = () => {
  cache.clear();
};

/**
 * Clear cache entries matching a prefix
 * @param {string} prefix - Key prefix to match
 */
const clearByPrefix = (prefix) => {
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) {
      cache.delete(key);
    }
  }
};

/**
 * Get cache statistics
 * @returns {object} - Cache stats
 */
const getStats = () => {
  let validEntries = 0;
  let expiredEntries = 0;
  const now = Date.now();
  
  for (const [key, entry] of cache.entries()) {
    if (now > entry.expiresAt) {
      expiredEntries++;
    } else {
      validEntries++;
    }
  }
  
  return {
    totalEntries: cache.size,
    validEntries,
    expiredEntries
  };
};

/**
 * Clean up expired entries
 */
const cleanup = () => {
  const now = Date.now();
  for (const [key, entry] of cache.entries()) {
    if (now > entry.expiresAt) {
      cache.delete(key);
    }
  }
};

/**
 * Fetch with cache - wrapper for fetch that uses cache
 * @param {string} cacheKey - Cache key
 * @param {Function} fetchFn - Async function that fetches data
 * @param {number} ttl - Time to live in milliseconds
 * @returns {Promise<any>} - Cached or fresh data
 */
const fetchWithCache = async (cacheKey, fetchFn, ttl = DEFAULT_TTL.medium) => {
  // Check cache first
  const cached = get(cacheKey);
  if (cached !== null) {
    return { data: cached, fromCache: true };
  }
  
  // Fetch fresh data
  const data = await fetchFn();
  
  // Cache the result
  set(cacheKey, data, ttl);
  
  return { data, fromCache: false };
};

/**
 * Create a cache key with parameters
 * @param {string} base - Base key
 * @param {object} params - Parameters to include in key
 * @returns {string} - Cache key
 */
const createKey = (base, params = {}) => {
  const paramStr = Object.entries(params)
    .filter(([_, v]) => v !== undefined && v !== null)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
  
  return paramStr ? `${base}?${paramStr}` : base;
};

// Auto-cleanup every 5 minutes
let cleanupInterval = null;
const startAutoCleanup = () => {
  if (cleanupInterval) return;
  cleanupInterval = setInterval(cleanup, 5 * 60 * 1000);
};

const stopAutoCleanup = () => {
  if (cleanupInterval) {
    clearInterval(cleanupInterval);
    cleanupInterval = null;
  }
};

// Start auto-cleanup on module load
startAutoCleanup();

export const AdminCache = {
  set,
  get,
  has,
  remove,
  clear,
  clearByPrefix,
  getStats,
  cleanup,
  fetchWithCache,
  createKey,
  startAutoCleanup,
  stopAutoCleanup,
  KEYS: CACHE_KEYS,
  TTL: DEFAULT_TTL
};

export default AdminCache;
